import { api } from "@/auth/api";
import { ParamHistoryRow, ParamImportHistoryRow } from "@/types";
import { Paths, UON } from "@/utils";

function unwrapExportedBase64(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.startsWith("\"")) {
    return JSON.parse(trimmed);
  }
  return trimmed.replace(/^"|"$/g, "");
}

function base64ToBytes(encoded: string): Uint8Array {
  const normalized = unwrapExportedBase64(encoded);
  const binary = atob(normalized);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function encodeJsonToBase64(json: string): string {
  return bytesToBase64(new TextEncoder().encode(json));
}

function isZipFile(file: File): boolean {
  const name = (file.name || "").toLowerCase();
  return name.endsWith(".zip") || file.type === "application/zip" || file.type === "application/x-zip-compressed";
}

function readFileAsBytes(file: File): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (result instanceof ArrayBuffer) {
        resolve(new Uint8Array(result));
        return;
      }
      reject(new Error("empty file"));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

function triggerDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function referenceDataUrl(path: string) {
  return `/${UON.SystemUid.odmReferenceData}/api/1/${UON.SystemUid.odmReferenceData}/${path}/`;
}

function mapImportRow(item: any): ParamHistoryRow {
  return {
    resourceUid: item.resourceUid,
    name: item.name,
    snapShot: item.snapShot ?? null,
    eventDate: item.eventDate ?? null,
    userName: item.userName ?? null,
    active: Boolean(item.active),
  };
}

function mapEventRow(item: any): ParamImportHistoryRow {
  return {
    resourceUid: item.resourceUid,
    name: item.name,
    importStatus: item.importStatus ?? null,
    userName: item.userName ?? null,
    importDate: item.importDate ?? null,
    paramHistoryId: item.paramHistoryId ?? null,
    errorMessage: item.errorMessage ?? null,
  };
}

export async function exportParam() {
  const { data } = await api().get(referenceDataUrl(Paths.exportParam), {
    responseType: "text",
    timeout: 0,
  });
  const bytes = base64ToBytes(String(data ?? ""));
  triggerDownload(new Blob([bytes], { type: "application/zip" }), "parametrage.zip");
}

export async function getImports(): Promise<ParamHistoryRow[]> {
  const { data } = await api().get(referenceDataUrl(Paths.imports));
  return (Array.isArray(data) ? data : []).map(mapImportRow);
}

export async function getEvents(): Promise<ParamImportHistoryRow[]> {
  const { data } = await api().get(referenceDataUrl(Paths.events));
  return (Array.isArray(data) ? data : []).map(mapEventRow);
}

export async function importParam(resourceUid: string) {
  const { data } = await api().put(
    `${referenceDataUrl(Paths.importParam)}${resourceUid}/`,
    {},
    { timeout: 0 }
  );
  return data;
}

export async function deleteImport(resourceUid: string) {
  const { data } = await api().delete(
    `${referenceDataUrl(Paths.paramHistory)}${resourceUid}/`
  );
  return data;
}

export async function createImport(payload: {
  name: string;
  jsonContent?: string;
  contentBase64?: string;
  userName: string;
  eventDate: string;
  snapShot?: string;
}) {
  const content = payload.contentBase64 || encodeJsonToBase64(payload.jsonContent || "");
  const { data } = await api().post(referenceDataUrl(Paths.paramHistory), {
    name: payload.name,
    content,
    active: false,
    userName: payload.userName,
    eventDate: payload.eventDate,
    snapShot: payload.snapShot ?? "",
    objectType: "odm.referencedata.paramhistory",
    systemUid: UON.SystemUid.odmReferenceData,
  });
  return data;
}

export { isZipFile, readFileAsBytes, bytesToBase64 };
